import React, {Fragment, useMemo} from 'react';

import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';

import {AutoSizedTextArea} from 'app/components/general/AutoSizedTextArea';
import {DebouncedInput} from 'app/general/primitives/DebouncedInput';
import {StepAutomationTitle} from 'app/procedures/components/structures';
import {SetStepLaneButton} from 'app/steps/components/edit/SetStepLaneButton';
import {gt} from 'app/utils/i18n';

import type {ApplyStepShaperFuncType} from 'app/steps/document-shaper';
import type {ProcedureStepType} from 'sweet-types';
import type {AutomationType} from 'sweet-types/procedure/procedure-step';

type OptionalFieldProps = {
  value: string;
  label: string;
  edit: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onRemove: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    label: string
  ) => void;
};

function OptionalEmailField({
  value,
  edit,
  label,
  placeholder,
  onChange,
  onRemove,
}: OptionalFieldProps) {
  return (
    <div>
      <label className="mb-1">{label}:</label>
      <div className="input-group">
        <DebouncedInput
          className="form-control"
          type="email"
          value={value}
          disabled={!edit}
          onChange={onChange}
          placeholder={placeholder ? placeholder : ''}
        />
        <UncontrolledDropdown className="input-group-append bg-white">
          <DropdownToggle
            color="outline-secondary dropdown-toggle border-left-0"
            caret={false}
            disabled={!edit}
            style={{borderColor: '#ced4da'}}
          >
            <i className="fa fa-ellipsis-v px-1" />
          </DropdownToggle>
          <DropdownMenu right>
            <DropdownItem
              onClick={(e) => onRemove(e, label)}
              className={'btn-sm'}
            >
              <i className="fa fa-fw fa-times" /> {gt('Remove')}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    </div>
  );
}

export function AutomationForm({
  edit,
  onChange,
  automation,
}: {
  edit: boolean;
  onChange?: (automation: AutomationType) => unknown;
  automation: AutomationType;
}) {
  const emailPlaceholder = gt('help@example.com, johndoe@example');
  const [optionalFields, setOptionalFields] = React.useState([
    {
      key: 'reply_to',
      label: gt('Reply To'),
      placeholder: emailPlaceholder,
      visible: automation.reply_to.length ? true : false,
    },
    {
      key: 'cc',
      label: gt('CC'),
      placeholder: emailPlaceholder,
      visible: automation.cc.length ? true : false,
    },
  ]);
  const mainFields = [
    {
      key: 'subject',
      label: gt('Subject'),
      type: 'text',
      placeholder: gt('Give your email a subject title'),
    },
    {
      key: 'send_to',
      label: gt('Send To'),
      type: 'email',
      placeholder: emailPlaceholder,
    },
  ];
  const optionalFieldsByVisibility = useMemo(
    () =>
      optionalFields.sort((a, b) =>
        a.visible === b.visible ? 0 : a.visible ? -1 : 1
      ),
    optionalFields
  );
  const toggleOptionalFieldVisibility = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    key: string
  ) => {
    e.preventDefault();
    setOptionalFields(
      optionalFields.map((field) => {
        if (field.key === key) {
          return {
            ...field,
            visible: !field.visible,
          };
        }
        return field;
      })
    );
    onChange && onChange({...automation, [key]: ''});
  };

  const handleUpdateField = (field: string, type: string, text: string) => {
    const data =
      type == 'email' ? text.split(',').map((email) => email.trim()) : text;
    onChange && onChange({...automation, [field]: data});
  };

  const handleUpdateContent = (value: string) => {
    if (onChange && value !== automation.content) {
      onChange({...automation, content: value});
    }
  };
  return (
    <div>
      <form>
        {edit && (
          <p>
            <strong>{gt('Note: ')}</strong>
            {gt(
              'For any dynamic user input, please wrap the text ' +
                'in double braces without spaces. Use underscores '
            )}
            &rsquo;<code>_</code>&lsquo;
            {gt(' if you need to separate words. For example: ')}
            <code>{`{{user_name}}`}</code>,<code>{`{{tracking_number}}`}</code>
          </p>
        )}
        {mainFields.map(({label, key, type, placeholder}) => (
          <div key={key} className="form-group mb-2">
            <label className="mb-1">{label}:</label>
            <DebouncedInput
              value={automation[key]}
              type={type}
              disabled={!edit}
              className="form-control"
              onChange={(value) => handleUpdateField(key, type, value)}
              placeholder={placeholder}
            />
          </div>
        ))}
        {optionalFieldsByVisibility.map(({label, placeholder, key, visible}) =>
          visible ? (
            <div key={key} className="form-group mb-2">
              <OptionalEmailField
                value={automation[key]}
                edit={edit}
                label={label}
                onRemove={(e) => toggleOptionalFieldVisibility(e, key)}
                onChange={(value) => handleUpdateField(key, 'email', value)}
                placeholder={placeholder}
              />
            </div>
          ) : edit ? (
            <button
              className="btn btn-link"
              onClick={(e) => toggleOptionalFieldVisibility(e, key)}
            >
              Add {label}
            </button>
          ) : null
        )}
        <div className="form-group mb-2">
          <label htmlFor="message" className="mb-1">
            Message:
          </label>
          <div className="mb-2">
            <AutoSizedTextArea
              id="message"
              placeholder="Your email content"
              className="form-control"
              disabled={!edit}
              value={automation['content']}
              onChange={handleUpdateContent}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onBlur={() => {}} // submit
            />
          </div>
        </div>
      </form>
    </div>
  );
}

type Props = {
  step: ProcedureStepType;
  automation: AutomationType;
  onChange: (question: AutomationType) => unknown;
  applyStepShaper: ApplyStepShaperFuncType;
  stepLaneId?: string | null;
};

export function EditableProcedureAutomationFooter({
  automation,
  applyStepShaper,
  onChange,
  stepLaneId,
}: Props) {
  return (
    <Fragment>
      <div className="mb-4">
        <StepAutomationTitle className="mt-3" title={gt('Email Automation')} />
        <AutomationForm
          automation={automation}
          edit={true}
          onChange={onChange}
        />
      </div>
      <div className="d-flex flex-wrap justify-content-end mb-2 pt-2 border-top">
        <div className="mr-1">
          <SetStepLaneButton
            applyStepShaper={applyStepShaper}
            stepLaneId={stepLaneId}
          />
        </div>
      </div>
    </Fragment>
  );
}
